import { useEffect, useRef } from 'react';
import type React from 'react';

import { focus } from '#src/app/shared/utils/dom';

/**
 * Hands focus back to `ref` when `isOpen` goes from `true` to `false`.
 *
 * A controlled modal gives react-aria no trigger element to restore to, so focus falls to `<body>`
 * on dismissal (WCAG 2.4.3, CHANGE-96802). Restoring is deferred one frame so it lands after
 * react-aria has torn the overlay down.
 *
 * The true→false guard is the point of the hook. Without it the effect also fires on a mount with
 * `isOpen === false` and steals focus from whatever the page had focused.
 */
export function useRestoreFocusOnClose(isOpen: boolean, ref: React.RefObject<HTMLElement | null>): void {
	const wasOpenRef = useRef(isOpen);

	useEffect(() => {
		const wasOpen = wasOpenRef.current;
		wasOpenRef.current = isOpen;

		if (isOpen || !wasOpen) return undefined;

		const raf = requestAnimationFrame(() => {
			focus(ref.current, { makeFocusable: true });
		});

		return () => cancelAnimationFrame(raf);
	}, [isOpen, ref]);
}
